import React, { useState, useCallback } from "react"
import { gql, useMutation, useQuery } from "@apollo/client"

import { CardHeading, Fieldset } from "components"
import { Modal } from "components"

import { Formik, Form, FieldArray } from "formik"

import * as yup from "yup"

import formatDate from "utils/formatDate"
import isWithinInterval from "date-fns/isWithinInterval"

import { toast } from "react-hot-toast"

const COUPONS = gql`
  query Trip($id: ID!) {
    trip(id: $id) {
      discounts {
        id
        code
        validFrom
        validTo
        amount
        unit
        availability
      }
    }
  }
`

const CREATE_TRIP_DISCOUNTS = gql`
  mutation CreateTripDiscounts($input: CreateTripDiscountsInput!) {
    createTripDiscounts(input: $input) {
      id
      code
      validFrom
      validTo
      amount
      unit
      availability
    }
  }
`

const UPDATE_TRIP_DISCOUNTS = gql`
  mutation UpdateTripDiscounts($input: UpdateTripDiscountsInput!) {
    updateTripDiscounts(input: $input) {
      id
      code
      validFrom
      validTo
      amount
      unit
      availability
    }
  }
`

const DELETE_TRIP_DISCOUNTS = gql`
  mutation DeleteTripDiscounts($input: DeleteTripDiscountsInput!) {
    deleteTripDiscounts(input: $input) {
      id
    }
  }
`

const CouponsContainer = ({ tripId }) => {
  const params = { variables: { id: tripId } }
  const { data, loading, error } = useQuery(COUPONS, params)
  const coupons = data?.trip?.discounts

  const [createTripDiscounts] = useMutation(CREATE_TRIP_DISCOUNTS)
  const [updateTripDiscounts] = useMutation(UPDATE_TRIP_DISCOUNTS)
  const [deleteTripDiscounts] = useMutation(DELETE_TRIP_DISCOUNTS)

  const handleCreate = async (discounts) => {
    const input = { tripId, discounts }

    const loadingId = toast.loading("Saving...")
    try {
      const coupon = await createTripDiscounts({
        variables: { input },
        update(cache, { data }) {
          const existingCoupons = cache.readQuery({
            query: COUPONS,
            variables: { id: tripId },
          })

          cache.writeQuery({
            query: COUPONS,
            variables: { id: tripId },
            data: {
              trip: {
                ...existingCoupons?.trip,
                discounts: [
                  ...existingCoupons?.trip?.discounts,
                  ...data?.createTripDiscounts,
                ],
              },
            },
          })
        },
      })
      toast.dismiss(loadingId)
      toast.success("Coupon created!")
      return coupon
    } catch (error) {
      toast.dismiss(loadingId)
      toast.error("Could not save.")
      throw error
    }
  }

  const handleUpdate = async (discounts) => {
    const input = { tripId, discounts }

    const loadingId = toast.loading("Saving...")
    try {
      const coupon = await updateTripDiscounts({
        variables: { input },
        update(cache, { data }) {
          const existingCoupons = cache.readQuery({
            query: COUPONS,
            variables: { id: tripId },
          })

          const coupons = existingCoupons?.trip?.discounts
          const updateCoupons = data?.updateTripDiscounts
          cache.writeQuery({
            query: COUPONS,
            variables: { id: tripId },
            data: {
              trip: {
                ...existingCoupons?.trip,
                discounts: [
                  ...coupons.map((item) => {
                    const isUpdate = updateCoupons.find((_) => _.id === item.id)
                    return isUpdate ? isUpdate : item
                  }),
                ],
              },
            },
          })
        },
      })
      toast.dismiss(loadingId)
      toast.success("Coupon created!")
      return coupon
    } catch (error) {
      toast.dismiss(loadingId)
      toast.error("Could not save.")
      throw error
    }
  }

  const handleDelete = async (discounts) => {
    const input = { tripId, discounts }

    const loadingId = toast.loading("Saving...")
    try {
      const coupon = await deleteTripDiscounts({ variables: { input } })
      toast.dismiss(loadingId)
      toast.success("Coupon deleted!")
      return coupon
    } catch (error) {
      toast.dismiss(loadingId)
      toast.error("Could not save.")
      throw error
    }
  }

  const [isEditing, setIsEditing] = useState(false)

  const openModal = useCallback(async () => {
    setIsEditing(true)
  }, [setIsEditing])

  const closeModal = useCallback(async () => {
    setIsEditing(false)
  }, [setIsEditing])

  const isEmpty = !data || data?.trip?.discounts?.length === 0

  return (
    <div className="bg-white rounded-lg shadow-lg">
      {isEmpty ? (
        <>
          <CardHeading title="Coupons" />
          <CouponsEmpty isLoading={loading} onClick={openModal} />
        </>
      ) : (
        <>
          <CardHeading title="Coupons" onClick={openModal} />
          <CouponsTable data={coupons} />
        </>
      )}

      <Modal title="Coupons" isOpen={isEditing} onClose={closeModal}>
        <div className="max-w-screen-lg mx-auto">
          <CouponsForm
            data={coupons}
            onClose={closeModal}
            onCreate={handleCreate}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
          />
        </div>
      </Modal>
    </div>
  )
}

export default CouponsContainer

const CouponsEmpty = ({ isLoading, onClick }) => {
  return (
    <div className="py-16 flex flex-col justify-center items-center">
      {isLoading ? (
        <div>...Loading...</div>
      ) : (
        <>
          <p className="text-center">No Coupons</p>

          <button
            onClick={onClick}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="-ml-1 mr-2 h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
              />
            </svg>
            Add coupons
          </button>
        </>
      )}
    </div>
  )
}
//
const CouponsTable = ({ data, onSelect }) => {
  const header = [
    "Code",
    "Valid from",
    "Valid to",
    "Value",
    "Availability",
    "Status",
  ]

  return (
    <table className="min-w-full table-auto">
      <thead>
        <tr>
          {header.map((item, index) => (
            <th
              key={index}
              className="px-6 py-3 border-b border-gray-200 bg-gray-100 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
            >
              {item}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="bg-white">
        {data?.map((discount, index) => (
          <tr key={index}>
            <td className="w-2/12 px-6 py-4 whitespace-no-wrap border-b border-gray-200">
              <p className="text-sm leading-5 font-medium text-gray-900">
                {discount.code}
              </p>
            </td>

            <td className="w-3/12 px-6 py-4 whitespace-no-wrap border-b border-gray-200">
              <p className="text-sm leading-5 text-gray-500">
                {formatDate(discount?.validFrom, "dd, MMMM yyyy")}
              </p>
            </td>

            <td className="w-3/12 px-6 py-4 whitespace-no-wrap border-b border-gray-200 text-sm leading-5 text-gray-500">
              <p className="text-sm leading-5 text-gray-500">
                {formatDate(discount?.validTo, "dd, MMMM yyyy")}
              </p>
            </td>

            <td className="w-2/12 px-6 py-4 whitespace-no-wrap border-b border-gray-200">
              <p className="text-sm leading-5 font-medium text-gray-900">
                {discount?.unit !== "PERCENT" && "$"}
                {discount.amount}
                {discount?.unit === "PERCENT" && "%"}
              </p>
            </td>

            <td className="w-2/12 px-6 py-4 whitespace-no-wrap border-b border-gray-200">
              <p className="text-sm leading-5 font-medium text-gray-900">
                {discount.availability ? discount.availability : "Unlimited"}
              </p>
            </td>

            <td className="w-2/12 px-6 py-4 whitespace-no-wrap border-b border-gray-200">
              <p className="text-sm leading-5 font-medium text-gray-900">
                {isWithinInterval(new Date(), {
                  start: new Date(discount.validFrom),
                  end: new Date(discount.validTo),
                }) ? (
                  <span className="bg-green-200 text-green-800 px-2 inline-flex rounded-full">
                    Active
                  </span>
                ) : (
                  <span className="bg-yellow-200 text-yellow-800 px-2 inline-flex rounded-full">
                    Inactive
                  </span>
                )}
              </p>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

const CouponsForm = ({ data, onClose, onCreate, onUpdate, onDelete }) => {
  const discountMock = {
    code: "TEST",
    validFrom: "2021-06-01",
    validTo: "2021-06-10",
    unit: "AMOUNT",
    amount: 100,
    availability: null,
  }

  const discountModel = {
    code: "",
    validFrom: "",
    validTo: "",
    unit: "AMOUNT",
    amount: 0,
    availability: null,
  }

  const couponSchema = yup.object({
    discounts: yup.array().of(
      yup.object({
        code: yup.string().required("Required"),
        validFrom: yup.string().required("Required"),
        validTo: yup.string().required("Required"),
        amount: yup.number().required("Required").min(1, "> 0"),
      }),
    ),
  })

  const initialValues = {
    discounts: [...(data && data.length > 0 ? data : [discountModel])].map(
      (item) => ({
        ...item,
        validFrom: formatDate(item.validFrom, "yyyy-LL-dd"),
        validTo: formatDate(item.validTo, "yyyy-LL-dd"),
      }),
    ),
  }

  const onSubmit = useCallback(async (values, actions) => {
    try {
      //This is expensive because everytime this functions gets called update will happen
      //TODO detect if values chaged, and then update shallowDeep(inital, current)

      actions.setSubmitting(true)

      const initialDataIds = data?.map((_) => _.id)
      const currentDataIds = values?.discounts?.map((_) => _.id)

      const coupons = values?.discounts?.map((_) => ({
        ..._,
      }))

      const couponsForCreate = coupons.filter((_) => !_.id)
      const couponsForUpdate = coupons.filter(
        (_) => _.id && initialDataIds?.includes(_.id),
      )
      const couponsForDelete = data?.filter(
        (_) => _.id && !currentDataIds?.includes(_.id),
      )

      if (couponsForCreate?.length > 0) {
        await onCreate(couponsForCreate)
      }

      if (couponsForUpdate?.length > 0) {
        await onUpdate(couponsForUpdate)
      }

      if (couponsForDelete?.length > 0) {
        await onDelete(couponsForDelete)
      }

      // actions.setSubmitting(false)
      onClose()
    } catch (e) {
      actions.setSubmitting(false)
      const errors = e.graphQLErrors.map((v) => v)
      actions.setFieldError("server", errors)
    }
  })

  const formikProps = {
    initialValues,
    validateOnBlur: false,
    validateOnChange: false,
    validationSchema: couponSchema,
    onSubmit,
  }

  return (
    <Formik {...formikProps}>
      {({ values, errors, isSubmitting }) => (
        <Form>
          <header className="w-full mb-4">
            <ul className="flex flex-wrap items-center  bg-gray-100">
              {[
                { name: "Code", size: "2" },
                { name: "Valid from", size: "3" },
                { name: "Valid to", size: "3" },
                { name: "Value", size: "2" },
                { name: "Availability", size: "2" },
              ].map((item, index) => (
                <li
                  key={index}
                  className={`w-${item.size}/12 px-4 py-3  border-b border-gray-200  text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider`}
                >
                  {item.name}
                </li>
              ))}
            </ul>
          </header>

          <FieldArray
            name="discounts"
            render={(arrayHelpers) => (
              <>
                {values?.discounts.map((information, index) => (
                  <FieldDiscount
                    key={index}
                    index={index}
                    title={information.title}
                    {...information}
                    onDelete={() => arrayHelpers.remove(index)}
                  />
                ))}

                <button
                  type="button"
                  onClick={() => arrayHelpers.push(discountModel)}
                  className="text-blue-500 hover:text-blue-700 py-2 px-1 my-6 rounded flex"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>

                  <span className="ml-2">Add coupon</span>
                </button>
              </>
            )}
          />

          {errors?.server?.map((error) => (
            <div role="alert">
              <div className="bg-red-500 text-white font-bold rounded-t px-4 py-2">
                {error.code}
              </div>
              <div className="border border-t-0 border-red-400 rounded-b bg-red-100 px-4 py-3 text-red-700">
                <p>{error.message}</p>
              </div>
            </div>
          ))}

          <ModalNavigation {...{ isSubmitting, onClose }} />
        </Form>
      )}
    </Formik>
  )
}

const ModalNavigation = ({ isSubmitting, onClose }) => {
  return (
    <div className=" px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
      <span className="flex w-full rounded-md shadow-sm sm:ml-3 sm:w-auto">
        <button
          disabled={isSubmitting}
          type="submit"
          className="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2 bg-green-600 text-base leading-6 font-medium text-white shadow-sm hover:bg-green-500 focus:outline-none focus:border-green-700 focus:shadow-outline-green transition ease-in-out duration-150 sm:text-sm sm:leading-5 disabled:opacity-50 disabled:cursor-wait"
        >
          Save
        </button>
      </span>

      <span className="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:w-auto">
        <button
          onClick={onClose}
          type="button"
          className="inline-flex justify-center w-full rounded-md border border-gray-300 px-4 py-2 bg-white text-base leading-6 font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue transition ease-in-out duration-150 sm:text-sm sm:leading-5"
        >
          Cancel
        </button>
      </span>
    </div>
  )
}

const FieldDiscount = ({ index, onDelete }) => {
  return (
    <div className="w-full">
      <div className="flex flex-wrap justify-end">
        <div className="w-2/12 px-2">
          <Fieldset placeholder="Code" name={`discounts[${index}].code`} />
        </div>

        <div className="w-3/12 px-2">
          <Fieldset
            type="date"
            placeholder="Valid from"
            name={`discounts[${index}].validFrom`}
          />
        </div>

        <div className="w-3/12 px-2">
          <Fieldset
            type="date"
            placeholder="Valid to"
            name={`discounts[${index}].validTo`}
          />
        </div>

        <div className="w-2/12 px-2 flex items-start justify-start space-x-2">
          <Fieldset
            type="number"
            placeholder="Value"
            name={`discounts[${index}].amount`}
          />

          <Fieldset
            type="select"
            options={[
              { label: "$", value: "AMOUNT" },
              { label: "%", value: "PERCENT" },
            ]}
            placeholder="Unit"
            name={`discounts[${index}].unit`}
          />
        </div>

        <div className="w-2/12 px-2 flex justify-between items-center space-x-2">
          <Fieldset
            type="number"
            placeholder="∞"
            name={`discounts[${index}].availability`}
          />

          <button type="button" onClick={onDelete}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  )
}
